import { useFormContext, useErrorMapping } from 'framework/form';
import { SubmitButton, Modal } from 'framework/ui/ui-builder';
import React, { useEffect, useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import BusinessRulesListField from './BusinessRulesListField';
import { Typography } from '@mui/material';

const NonStrictRulesHandler = (props: {
  nonStrictRules: any;
  title: any;
  errorMap: any;
  children: any;
  ignoreNotStrictSource?: string;
}) => {
  const { nonStrictRules, title, errorMap, children, ignoreNotStrictSource = 'ignoreNotStrictRules' } = props;

  const areOnlyNotStrictRulesViolated = (allErrorCodes: any[]) => {
    return allErrorCodes.length && allErrorCodes.every(code => nonStrictRules.includes(code));
  };

  const { addValidationErrorListener, removeValidationErrorListener, submitForm, onChangeCallback, isSubmitEnabled } =
    useFormContext();

  const { getErrorCode } = useErrorMapping();

  const [violatedRules, setViolatedRules] = useState([]);

  const closeModal = () => {
    setViolatedRules([]);
    if (onChangeCallback) {
      onChangeCallback(
        {
          [ignoreNotStrictSource]: false,
        },
        true,
      );
    }
  };

  const openModal = (rules: React.SetStateAction<never[]>) => {
    setViolatedRules(rules);

    if (onChangeCallback) {
      onChangeCallback(
        {
          [ignoreNotStrictSource]: true,
        },
        true,
      );
    }
  };

  const isModalOpened = () => {
    return !!violatedRules.length;
  };

  const handleError = (errors: any[]): void => {
    const errorCodes = errors.map(error => getErrorCode(error));

    if (areOnlyNotStrictRulesViolated(errorCodes)) {
      openModal(errors as never[]);
    } else {
      closeModal();
    }
  };

  useEffect(() => {
    if (addValidationErrorListener) {
      // @ts-ignore
      addValidationErrorListener(handleError);
    }

    return () => {
      if (removeValidationErrorListener) {
        // @ts-ignore
        removeValidationErrorListener(handleError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continueButton = (
    <SubmitButton
      key="btn-continue"
      onClick={submitForm}
      isLoading={!isSubmitEnabled}
      data-testid="not-strict-rules-continue"
    />
  );

  const cancelButton = (
    <Button
      onClick={() => {
        closeModal();
      }}
      data-testid="not-strict-rules-continue"
      key="btn-cancel"
      color="secondary"
      disabled={!isSubmitEnabled}
    >
      Cancel
    </Button>
  );

  return (
    <Modal
      isOpen={isModalOpened()}
      title="Business rules violated"
      buttons={[continueButton, cancelButton]}
      onToggle={() => closeModal()}
      key="modal-not-strict"
      hasCancelButton={false}
    >
      <Typography variant="body1" component="span">
        {title}
      </Typography>

      <BusinessRulesListField value={violatedRules} errorMap={errorMap} />

      {children}
    </Modal>
  );
};

export default NonStrictRulesHandler;
